import { createFileRoute, notFound, Outlet } from "@tanstack/react-router"
import { FilterBar } from "@/components/layout/filter-bar"
import { ListEmpty } from "@/components/layout/list-empty"
import { requirePlanPages } from "@/routes/_layout/planning/-lib/gate"
import {
  displayOf,
  planSearchSchema,
} from "@/routes/_layout/planning/-lib/plan-search"
import { findPlan, usePlan } from "@/routes/_layout/planning/-lib/store"
import ViewTimelineOutline from "~icons/material-symbols/view-timeline-outline"
import { Board } from "./-components/board"
import { PlanToolbar } from "./-components/plan-toolbar"
import { Timeline } from "./-components/timeline"

export const Route = createFileRoute("/_layout/planning_/$planId")({
  validateSearch: (search) => planSearchSchema.parse(search),
  beforeLoad: ({ context: { queryClient } }) => requirePlanPages(queryClient),
  loaderDeps: ({ search }) => ({ focus: search.focus }),
  loader: ({ params, deps, location }) => {
    const plan = findPlan(params.planId)
    if (!plan) throw notFound()
    const step = plan.steps.find((each) => each.id === deps.focus)
    const label = `${plan.id} · ${plan.tail} · ${plan.title}`
    return {
      breadcrumb: [
        { label: "Planning", href: "/planning" },
        step ? { label, href: location.pathname } : { label },
        ...(step ? [{ label: step.name }] : []),
      ],
    }
  },
  component: PlanPage,
})

function PlanPage(): React.ReactElement {
  const { planId } = Route.useParams()
  const plan = usePlan(planId)
  const display = displayOf(Route.useSearch())

  if (!plan) {
    return (
      <ListEmpty
        icon={ViewTimelineOutline}
        title="Plan not found"
        body="This plan may have been removed."
      />
    )
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <FilterBar>
        <PlanToolbar plan={plan} display={display} />
      </FilterBar>
      <div className="flex min-h-0 flex-1">
        <div className="min-w-0 flex-1">
          {display.view === "board" ? (
            <Board plan={plan} />
          ) : (
            <Timeline plan={plan} display={display} />
          )}
        </div>
        <Outlet />
      </div>
    </div>
  )
}
